// Flow-by-point maths: from the hourly rows the API returns for the nearest road to one series per
// direction on a fixed hour grid. Pure: no React, router or fetch, and nothing here reads the
// machine's time zone (the tests also run under TZ=Pacific/Auckland).
import type { FlowRow } from '../services/flowApi';
import { parseBucket } from './dateWindow';
import type { TimeWindow } from './dateWindow';

export type FlowDirection = 1 | -1;
export const FLOW_DIRECTIONS: readonly FlowDirection[] = [1, -1];
// The backend does not say which compass direction 1 and -1 are, so they are named neutrally.
export const DIRECTION_LABELS: Readonly<Record<FlowDirection, string>> = { 1: 'Direction A', [-1]: 'Direction B' };
/** A road snapped further than this from the clicked point gets a warning. */
export const FAR_SNAP_METRES = 200;

const HOUR_MS = 3_600_000;

export interface FlowRoad {
  linkId: string;
  name: string | null;
  type: string | null;
  distanceM: number | null;
}

export type FlowOutcome =
  | { kind: 'empty'; road: FlowRoad | null }
  | {
      kind: 'data';
      road: FlowRoad;
      /** UTC ms of each hour start. */
      slots: readonly number[];
      /** Same length as `slots`; null = no published figure for that hour (a gap, not zero). */
      series: Readonly<Record<FlowDirection, readonly (number | null)[]>>;
      totals: Readonly<Record<FlowDirection, number | null>>;
      total: number | null;
      duplicatesRemoved: number;
      ignoredRows: number;
    };

const hasHour = (r: FlowRow) => r.time_bucket != null && r.direction != null;

/**
 * No traffic to show: no rows at all (LAT and LTU return nothing for a road without traffic), or
 * only the placeholder row NOR and EST return when no road in the country has traffic in the window.
 */
export function isEmptyFlow(rows: readonly FlowRow[]): boolean {
  return !rows.some(hasHour);
}

const bucketMs = (r: FlowRow) => parseBucket(r.time_bucket);

/**
 * Consecutive LAT/LTU batches overlap around midnight, so the same (road, hour, direction) can
 * arrive twice with different values. The batch that owns the hour always has the larger figure
 * (checked in BigQuery for every duplicated key), so the larger `people` is kept. A null counts as
 * smaller than any number; 0 is a number. The first row's position is kept.
 */
export function dedupeFlowRows(rows: readonly FlowRow[]): { rows: FlowRow[]; removed: number } {
  const out: FlowRow[] = [];
  const at = new Map<string, number>();
  for (const r of rows) {
    const ms = bucketMs(r);
    if (ms === null || r.direction == null) {
      out.push(r);
      continue;
    }
    const key = `${r.link_id}|${ms}|${r.direction}`;
    const i = at.get(key);
    if (i === undefined) {
      at.set(key, out.length);
      out.push(r);
    } else if (r.people != null && (out[i].people == null || r.people > (out[i].people as number))) {
      out[i] = r;
    }
  }
  return { rows: out, removed: rows.length - out.length };
}

/** UTC ms of each hour start in the window: 24 slots, or 23 / 25 on a daylight-saving day. */
export function hourSlots(w: TimeWindow): number[] {
  const start = Date.parse(w.start_time);
  const end = Date.parse(w.end_time);
  const slots: number[] = [];
  if (Number.isNaN(start) || Number.isNaN(end)) return slots;
  for (let t = start; t < end + 1; t += HOUR_MS) slots.push(t);
  return slots;
}

/** The road of the first row that names one. */
export function roadFromRows(rows: readonly FlowRow[]): FlowRoad | null {
  const r = rows.find((x) => x.link_id != null);
  if (!r || r.link_id == null) return null;
  return { linkId: r.link_id, name: r.link_name, type: r.link_type, distanceM: r.distance_m };
}

const sum = (xs: readonly (number | null)[]): number | null => {
  let total: number | null = null;
  for (const x of xs) if (x != null) total = (total ?? 0) + x;
  return total;
};

export function buildFlowSeries(rows: readonly FlowRow[], w: TimeWindow, o: { dedupe: boolean }): FlowOutcome {
  const road = roadFromRows(rows);
  if (isEmptyFlow(rows) || !road) return { kind: 'empty', road };

  const deduped = o.dedupe ? dedupeFlowRows(rows) : { rows: rows.slice(), removed: 0 };
  const slots = hourSlots(w);
  const slotAt = new Map(slots.map((ms, i) => [ms, i] as const));
  const series: Record<FlowDirection, (number | null)[]> = {
    1: slots.map(() => null),
    [-1]: slots.map(() => null),
  };

  let kept = 0;
  let ignoredRows = 0;
  for (const r of deduped.rows) {
    const ms = bucketMs(r);
    const i = ms === null ? undefined : slotAt.get(ms);
    if (r.link_id !== road.linkId || (r.direction !== 1 && r.direction !== -1) || i === undefined) {
      ignoredRows += 1;
      continue;
    }
    series[r.direction][i] = r.people;
    kept += 1;
  }
  if (kept === 0) return { kind: 'empty', road };

  const totals = { 1: sum(series[1]), [-1]: sum(series[-1]) } as Record<FlowDirection, number | null>;
  return {
    kind: 'data',
    road,
    slots,
    series,
    totals,
    total: sum([totals[1], totals[-1]]),
    duplicatesRemoved: deduped.removed,
    ignoredRows,
  };
}

/** '21:00' */
export function utcHourLabel(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

/** '38 m' under a kilometre, else '1.2 km'. */
export function formatDistance(m: number): string {
  const rounded = Math.round(m);
  if (rounded < 1000) return `${rounded} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

export function isFarSnap(m: number | null): boolean {
  return m != null && m > FAR_SNAP_METRES;
}

/** 'motorway_link' -> 'Motorway link'. */
export function roadTypeLabel(t: string | null): string | null {
  if (!t) return null;
  const s = t.replace(/_/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : null;
}
